import { type Editor as TiptapEditor, type JSONContent } from '@tiptap/core';
import { EditorContent, useEditor } from '@tiptap/react';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { templateMediaBase, uploadTemplateFile } from '@/entities/template';
import { useErrorMessage } from '@/shared/i18n';
import { useToast } from '@/shared/ui';

import styles from './DocumentEditor.module.css';
import { buildExtensions } from './extensions';
import { SelectionMenu } from './SelectionMenu';
import { Toolbar } from './Toolbar';

/**
 * The same rich-text editor, working directly on a document TEMPLATE's content: no note, no
 * collaboration room, nothing synchronised. The parent reads the result with `editor.getJSON()`
 * when the person saves; cancelling simply discards the editor. Files are added to the template
 * itself and are served through it.
 */
export function StandaloneEditor({
  templateId,
  nodes,
  onEditor,
  onChange,
}: {
  templateId: string;
  nodes: readonly JSONContent[];
  onEditor: (editor: TiptapEditor) => void;
  onChange: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const message = useErrorMessage();
  const extensions = useMemo(
    () =>
      buildExtensions({
        documentId: templateId,
        placeholder: t('documents.editor.placeholder'),
        mediaBase: templateMediaBase(templateId),
      }),
    // the language of the placeholder need not rebuild the editor
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [templateId],
  );
  const editor = useEditor(
    {
      extensions,
      content: { type: 'doc', content: [...nodes] },
      onUpdate: onChange,
      editorProps: {
        attributes: {
          class: styles.prose ?? '',
          'aria-label': t('documents.editor.content'),
          'data-testid': 'template-content',
        },
      },
    },
    [extensions],
  );
  useEffect(() => {
    onEditor(editor);
  }, [editor, onEditor]);
  const insert = useCallback(
    async (files: readonly File[]) => {
      for (const file of files) {
        try {
          const media = await uploadTemplateFile(templateId, file);
          const node = media.is_image
            ? { type: 'documentImage', attrs: { mediaId: media.media_id, alt: media.filename } }
            : {
                type: 'documentFile',
                attrs: {
                  mediaId: media.media_id,
                  filename: media.filename,
                  size: media.size_bytes,
                  mime: media.mime_type,
                },
              };
          editor.chain().focus().insertContentAt(editor.state.selection.to, node).run();
        } catch (error) {
          toast.show({ message: message(error) });
        }
      }
    },
    [editor, message, templateId, toast],
  );
  return (
    <div className={styles.standalone}>
      <Toolbar
        editor={editor}
        onImage={(files) => {
          void insert([...files]);
        }}
        onFile={(files) => {
          void insert([...files]);
        }}
      />
      <div className={styles.canvas}>
        <div className={styles.page}>
          <EditorContent editor={editor} />
          <SelectionMenu editor={editor} />
        </div>
      </div>
    </div>
  );
}
